/** Fase B (06/08/2026) -- datos reales de la nueva estructura de estrategias, mismos
 * endpoints que consume el Dashboard (vía `/api/mini-app/...`, que reutiliza literalmente
 * las mismas funciones de `app/api/dashboard.py`) -- coherencia total garantizada. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'
import type { CategoryPerformanceStats } from '@/types/miniApp'
import type {
  ChallengeHistoryResponse,
  ChallengeSummary,
  DailyRunTodayResponse,
  MatchLabTodayResponse,
  RunsTodayResponse,
  StrategyComparatorResponse,
  StrategyKey,
  TimeWindow,
} from '@/types/strategies'

export function useStrategiesComparator(window: TimeWindow = 'total') {
  return useQuery<StrategyComparatorResponse>({
    queryKey: ['mini-app', 'strategies-comparator', window],
    queryFn: async () => (await apiClient.get('/api/mini-app/strategies/comparator', { params: { window } })).data,
    staleTime: 60_000,
  })
}

export function useStrategyPerformance(strategyKey: StrategyKey, window: TimeWindow = 'total') {
  return useQuery<CategoryPerformanceStats>({
    queryKey: ['mini-app', 'strategy-performance', strategyKey, window],
    queryFn: async () =>
      (await apiClient.get(`/api/mini-app/strategies/${strategyKey}/performance`, { params: { window } })).data,
    staleTime: 60_000,
  })
}

export function useChallenges() {
  return useQuery<ChallengeSummary[]>({
    queryKey: ['mini-app', 'challenges'],
    queryFn: async () => (await apiClient.get('/api/mini-app/challenges')).data,
    staleTime: 30_000,
  })
}

export function useChallengeHistory(challengeId: number | null) {
  return useQuery<ChallengeHistoryResponse>({
    queryKey: ['mini-app', 'challenge-history', challengeId],
    queryFn: async () => (await apiClient.get(`/api/mini-app/challenges/${challengeId}/history`)).data,
    enabled: challengeId != null,
    staleTime: 60_000,
  })
}

export interface CurrentWeekChallengeResponse {
  configured: boolean
  week_start_date: string
  week_end_date: string
  challenge: ChallengeSummary | null
}

// Fase R.11 (Bloque D/E, 17/08/2026): Reto John Anthony SEMANAL -- reemplaza a
// `officialChallenge = challenges?.find(...)` en el Home (que podía mostrar un reto de una
// semana pasada, ya COMPLETED/FAILED, como si fuera "el de ahora"). `configured=false` es un
// estado válido: el usuario todavía no definió su capital para esta semana.
export function useCurrentWeekChallenge() {
  return useQuery<CurrentWeekChallengeResponse>({
    queryKey: ['mini-app', 'challenge-current-week'],
    queryFn: async () => (await apiClient.get('/api/mini-app/challenge/current-week')).data,
    staleTime: 30_000,
  })
}

export function useSetupWeeklyChallenge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (initialBankroll: number) =>
      (await apiClient.post('/api/mini-app/challenge/setup', { initial_bankroll: initialBankroll })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'challenge-current-week'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'challenges'] })
    },
  })
}

export function useRunsToday() {
  return useQuery<RunsTodayResponse>({
    queryKey: ['mini-app', 'runs-today'],
    queryFn: async () => (await apiClient.get('/api/mini-app/runs/today')).data,
    staleTime: 60_000,
  })
}

/** P0.31 (26/08/2026, rediseño Mini App -- Sección 1 del encargo): reemplaza `useRunsToday`
 * en la UI activa -- una sola Daily Run oficial (12:00 Europe/Madrid), nunca 2 franjas. */
export function useDailyRunToday() {
  return useQuery<DailyRunTodayResponse>({
    queryKey: ['mini-app', 'daily-run-today'],
    queryFn: async () => (await apiClient.get('/api/mini-app/daily-run/today')).data,
    staleTime: 60_000,
  })
}

/** P0.31 (26/08/2026, rediseño Mini App -- Sección 7 del encargo, "Match Lab"). */
export function useMatchLabToday() {
  return useQuery<MatchLabTodayResponse>({
    queryKey: ['mini-app', 'match-lab-today'],
    queryFn: async () => (await apiClient.get('/api/mini-app/match-lab/today')).data,
    staleTime: 60_000,
  })
}

// ─── Mi Reto -- movimientos de capital (P0.21 backend, P0.23 Sección 14 del encargo) ───────
// Deliberadamente SOLO operan sobre el Reto personal vigente de este usuario --
// `_get_users_active_personal_challenge` en el backend ya rechaza cualquier intento sobre el
// Reto Oficial (`mini_app_user_id IS NULL`) como segunda barrera real, nunca solo del lado UI.

export interface ChallengeTransaction {
  id: number
  transaction_type: string
  amount: number
  balance_before: number
  balance_after: number
  notes: string | null
  created_at: string
}

export function useChallengeTransactions(challengeId: number | null) {
  return useQuery<ChallengeTransaction[]>({
    queryKey: ['mini-app', 'challenge-transactions', challengeId],
    queryFn: async () => (await apiClient.get(`/api/mini-app/challenge/${challengeId}/transactions`)).data,
    enabled: challengeId != null,
    staleTime: 15_000,
  })
}

interface ChallengeMovementPayload {
  amount: number
  idempotency_key?: string
  notes?: string
}

export function useDepositToChallenge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ChallengeMovementPayload) =>
      (await apiClient.post('/api/mini-app/challenge/deposit', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'challenge-current-week'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'challenges'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'challenge-transactions'] })
    },
  })
}

export function useWithdrawFromChallenge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ChallengeMovementPayload) =>
      (await apiClient.post('/api/mini-app/challenge/withdraw', payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'challenge-current-week'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'challenges'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'challenge-transactions'] })
    },
  })
}

export function useResetChallenge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (reason?: string) =>
      (await apiClient.post('/api/mini-app/challenge/reset', { reason })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'challenge-current-week'] })
      queryClient.invalidateQueries({ queryKey: ['mini-app', 'challenges'] })
    },
  })
}
